import { useEffect, useRef, useCallback } from "react";
import { useReportJob } from "@/export/api/queries/reports.queries";
import { ReportJob, JobStatus } from "@/export/types/reports.ts";

interface UseReportJobPollingOptions {
  jobId: string;
  onStatusChange?: (status: JobStatus, job: ReportJob) => void;
  onComplete?: (job: ReportJob) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

export const useReportJobPolling = ({
  jobId,
  onStatusChange,
  onComplete,
  onError,
  enabled = true,
}: UseReportJobPollingOptions) => {
  const previousStatusRef = useRef<JobStatus | null>(null);
  const isCompleteRef = useRef(false);

  const {
    data: jobResponse,
    error,
    isLoading,
    isError,
  } = useReportJob(jobId, enabled);

  const job = jobResponse?.data;
  const currentStatus = job?.status;

  // Handle status changes
  useEffect(() => {
    if (!job || !currentStatus) return;

    const previousStatus = previousStatusRef.current;
    
    // Check if status changed
    if (previousStatus !== currentStatus) {
      previousStatusRef.current = currentStatus;
      onStatusChange?.(currentStatus, job);
    }

    // Check if job completed
    if ((currentStatus === 'SUCCESS' || currentStatus === 'FAILED') && !isCompleteRef.current) {
      isCompleteRef.current = true;
      onComplete?.(job);
    }
  }, [job, currentStatus, onStatusChange, onComplete]);

  // Handle errors
  useEffect(() => {
    if (isError && error) {
      onError?.(error);
    }
  }, [isError, error, onError]);

  // Reset completion flag when jobId changes
  useEffect(() => {
    isCompleteRef.current = false;
    previousStatusRef.current = null;
  }, [jobId]);

  const isActive = currentStatus === 'PENDING' || currentStatus === 'RUNNING';
  const isCompleted = currentStatus === 'SUCCESS' || currentStatus === 'FAILED';
  const isSuccessful = currentStatus === 'SUCCESS';
  const isFailed = currentStatus === 'FAILED';

  return {
    job,
    status: currentStatus,
    progress: job?.progress,
    artifact: job?.artifact,
    isLoading,
    isError,
    error,
    isActive,
    isCompleted,
    isSuccessful,
    isFailed,
    canDownload: isSuccessful && job?.artifact?.exists,
  };
};

